import { Part } from "../skeleton.ts";
import type { FighterState, Limb } from "../state.ts";
import { tuning } from "../tuning.ts";

export interface LimbFactors {
  speed: number;
  mass: number;
  disabled: boolean;
}

/** How injury and exhaustion change what a limb can do. */
export function limbFactors(f: FighterState, limb: Limb): LimbFactors {
  const inj = tuning.injury;
  const below = tuning.hp.injuredBelow;
  let speed = 1;
  let mass = 1;
  let disabled = false;
  if (limb < 2) {
    const upper = f.hp[Part.UpperArmL + limb] ?? 0;
    const fore = f.hp[Part.ForearmL + limb] ?? 0;
    if (upper <= 0 || fore <= 0) disabled = true;
    if (upper < tuning.hp.upperArm * below) {
      speed *= inj.upperArmSpeed;
      mass *= inj.upperArmMass;
    }
    if (fore < tuning.hp.forearm * below) speed *= inj.forearmSpeed;
  } else {
    const side = limb - 2;
    const thigh = f.hp[Part.ThighL + side] ?? 0;
    const shin = f.hp[Part.ShinL + side] ?? 0;
    if (thigh <= 0 || shin <= 0) disabled = true;
    if (thigh < tuning.hp.thigh * below) speed *= inj.thighKickSpeed;
    if (shin < tuning.hp.shin * below) speed *= inj.shinKickSpeed;
  }
  if (f.exhausted) speed *= tuning.stamina.exhaustedSpeed;
  return { speed, mass, disabled };
}

export function isDazed(f: FighterState): boolean {
  return (f.hp[Part.Head] ?? 0) < tuning.hp.head * tuning.hp.injuredBelow;
}

/** True while the fighter is loading or winding up a strike (counter-hit window). */
export function inWindup(f: FighterState): boolean {
  return (
    f.chargeArm !== null ||
    f.chargeLeg !== null ||
    f.strikeArm?.phase === "windup" ||
    f.strikeLeg?.phase === "windup"
  );
}

/** True while the fighter recovers from a strike that missed or was blocked (counter-hit window). */
export function openAfterMiss(f: FighterState): boolean {
  for (const s of [f.strikeArm, f.strikeLeg]) {
    if (s?.phase === "recovery" && (s.whiffed || s.blocked)) return true;
  }
  return false;
}
